import React, {useState, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import Button from './Button';
import css from './SplitButton.module.scss';
import ChevronDown from '../Icons/ChevronDown.jsx';

const SplitButton = ({
  children,
  onClick,
  icon,
  variant = 'primary',
  size = 'default',
  disabled = false,
  className = '',
  options = [],
  position = 'below',
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleMainClick = (e) => {
    e.preventDefault();
    if (onClick && !disabled) {
      onClick();
    }
  };

  const handleDropdownToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleOptionClick = (optionOnClick) => {
    setIsOpen(false);
    if (optionOnClick) {
      optionOnClick();
    }
  };

  const dropdownClasses = [
    css.dropdown,
    position === 'above' ? css.dropdownAbove : css.dropdownBelow
  ].filter(Boolean).join(' ');

  const wrapperClasses = [
    css.splitButtonWrapper,
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses} ref={wrapperRef}>
      <div className={css.buttonGroup}>
        <Button
          {...props}
          variant={variant}
          size={size}
          disabled={disabled}
          onClick={handleMainClick}
          className={css.mainButton}
          icon={icon}
        >
          {children}
        </Button>
        <button
          type="button"
          className={`${css.dropdownTrigger} ${css[variant]} ${css[size]} ${disabled ? css.disabled : ''}`}
          onClick={handleDropdownToggle}
          disabled={disabled}
        >
          <span className={css.dropdownArrow}>
            <ChevronDown width="15px" />
          </span>
        </button>
      </div>
      {isOpen && options.length > 0 && (
        <div className={dropdownClasses}>
          {options.map((option, index) => (
            <div
              key={index}
              className={css.option}
              onClick={() => handleOptionClick(option.onClick)}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

SplitButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  icon: PropTypes.node,
  variant: PropTypes.oneOf([
    'primary',
    'secondary',
    'tertiary',
    'link',
    'success',
    'warning',
    'danger',
    'ghost'
  ]),
  size: PropTypes.oneOf(['small', 'default']),
  disabled: PropTypes.bool,
  className: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      onClick: PropTypes.func.isRequired,
    })
  ),
  position: PropTypes.oneOf(['above', 'below']),
};

export default SplitButton;

